import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { getAnalytics, logEvent } from 'firebase/analytics';
import ArrowRight from '@spectrum-icons/workflow/ArrowRight';
import { UpgradeModal } from './UpgradeModal';
import { useCommanderTunedAvailable } from './useCommanderTunedAvailable';
import './UpgradeCTA.css';

// Modes that pull from commander-tuned aggregate stats — useless without them.
const TUNED_MODES = new Set(['staples', 'synergy', 'manabase', 'archetype', 'combos']);
const presetNeedsTuned = (preset) => !!(preset && preset.mode && TUNED_MODES.has(preset.mode));

/**
 * Drop-in "you're here → click for cards to get there" trigger. Opens the
 * Upgradelizer in a dialog pre-filtered by `preset`, keeping the user in the
 * current diagnosis instead of switching tabs.
 *
 * Self-gates on commander tuned-stats: if `preset` needs tuned data and the
 * commander has none, it swaps in `fallback` (a role-based preset that always
 * works) or hides entirely — so we never open a modal that dead-ends on
 * "pick a category". A still-loading health probe is treated optimistically.
 *
 * @param {object} props.preset    Filter seed for UpgradelizerPane.
 * @param {object} [props.fallback] Role-based preset to use when tuned data is absent.
 * @param {React.ReactNode} [props.fallbackRender] Rendered in place of the CTA when a
 *   tuned preset can't run and there's no `fallback` — e.g. the plain diagnosis text,
 *   so an inline suggestion stays visible (just non-clickable) instead of vanishing.
 * @param {string} props.label     Button copy, e.g. "Find synergy upgrades".
 * @param {string} [props.title]   Dialog heading; defaults to `label`.
 * @param {'primary'|'inline'|'ghost'} [props.variant] Visual treatment.
 * @param {string} [props.className] Extra classes for layout placement.
 * @param {string} [props.origin]  Launching pane id (e.g. 'manabase',
 *   'powerlevels', 'synergy'). Fires a `suggestions_click_<origin>` analytics
 *   event on open and threads through to the TCG buy-click attribution.
 */
export function UpgradeCTA({
    preset = null,
    fallback = null,
    fallbackRender,
    label = 'Find upgrades',
    title,
    variant = 'inline',
    className = '',
    origin = null,
}) {
    const [open, setOpen] = useState(false);
    const commanderId = useSelector((s) => s.deck.deck?.commanderIndexId);
    const tunedAvailable = useCommanderTunedAvailable(commanderId);

    const handleOpen = () => {
        if (origin) {
            // Fire-and-forget: a telemetry hiccup must never block the modal.
            try {
                logEvent(getAnalytics(), `suggestions_click_${origin}`);
            } catch (_err) {
                // analytics not initialized / blocked — ignore
            }
        }
        setOpen(true);
    };

    let effectivePreset = preset;
    if (presetNeedsTuned(preset) && tunedAvailable === false) {
        if (fallback) effectivePreset = fallback;
        else if (fallbackRender !== undefined) return <>{fallbackRender}</>;
        else return null;
    }

    return (
        <>
            <button
                type="button"
                className={`upgradeCta upgradeCta--${variant}${className ? ` ${className}` : ''}`}
                onClick={handleOpen}
            >
                <span className="upgradeCtaLabel">{label}</span>
                <ArrowRight size="XS" UNSAFE_className="upgradeCtaArrow" />
            </button>
            {open && (
                <UpgradeModal
                    isOpen={open}
                    onClose={() => setOpen(false)}
                    preset={effectivePreset}
                    title={title || label}
                    origin={origin}
                />
            )}
        </>
    );
}
